import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGameStore, useIsHost, useCurrentQuestion } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import { ROUND_DURATION_MS } from '@youraiquiz/shared';

const GameLoopView = () => {
  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const question = useCurrentQuestion();
  const { answer, kickPlayer } = useGameActions();
  const { leaveRoom } = useSocket();

  const [timeLeft, setTimeLeft] = useState(ROUND_DURATION_MS);
  const [selected, setSelected] = useState<number | null>(null);
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  // Check if dev mode (no timer) - indicated by currentRoundEndTime === 0
  const isDevMode = gameState.currentRoundEndTime === 0;

  // Check if current player has already answered
  const myAnswer = clientState.myId ? gameState.playerAnswers[clientState.myId] : null;

  useEffect(() => {
    // Reset selection when question changes
    setSelected(myAnswer?.optionIndex ?? null);

    // Skip timer loop in dev mode
    if (isDevMode) {
      setTimeLeft(0);
      return;
    }

    const end = gameState.currentRoundEndTime;

    const update = () => {
      const now = Date.now();
      const diff = Math.max(0, end - now);
      setTimeLeft(diff);
      if (diff > 0) {
        timerRef.current = requestAnimationFrame(update);
      }
    };

    timerRef.current = requestAnimationFrame(update);
    return () => {
      if (timerRef.current !== null) cancelAnimationFrame(timerRef.current);
    };
  }, [gameState.currentQuestionIndex, gameState.currentRoundEndTime, myAnswer?.optionIndex, isDevMode]);

  const handleSelect = (idx: number) => {
    if (selected !== null || myAnswer) return;
    setSelected(idx);
    answer(idx);
    if (navigator.vibrate) navigator.vibrate(20);
  };

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  if (!question) return null;

  const progress = isDevMode ? 100 : (timeLeft / ROUND_DURATION_MS) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="w-full max-w-md h-full flex flex-col justify-between py-4 px-2"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors mb-2"
      >
        LEAVE ROOM
      </button>

      {/* Header */}
      <div className="space-y-4">
        <div className="w-full glass p-3 rounded-2xl border-b-4 border-white/10 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="bg-neon-blue px-2 py-0.5 rounded text-[10px] font-black uppercase inline-block self-start mb-1">
              {question.category}
            </span>
            <span className="text-gray-400 font-bold text-xs uppercase tracking-widest">
              Question {gameState.currentQuestionIndex + 1}/{gameState.questions.length}
            </span>
          </div>
          {isDevMode ? (
            <div className="text-2xl font-black text-amber-400 bg-amber-400/20 px-3 py-1 rounded-lg">
              DEV
            </div>
          ) : (
            <div className={`text-3xl font-black ${timeLeft < 5000 ? 'text-red-500' : 'text-neon-purple'}`}>
              {(timeLeft / 1000).toFixed(0)}s
            </div>
          )}
        </div>

        {/* Timer Bar - hidden in dev mode */}
        {!isDevMode && (
          <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden border border-white/10">
            <motion.div
              initial={{ width: '100%' }}
              animate={{ width: `${progress}%` }}
              className={`h-full ${progress < 30 ? 'bg-red-500' : 'bg-neon-blue'}`}
            />
          </div>
        )}

        {/* Question */}
        <div className="text-center py-4 px-2">
          <h2 className="text-2xl font-black leading-tight tracking-tight drop-shadow-md">
            {question.text}
          </h2>
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 gap-3 w-full pb-6">
        {question.options.map((opt, i) => (
          <button
            key={i}
            disabled={selected !== null}
            onClick={() => handleSelect(i)}
            className={`p-4 rounded-2xl text-lg font-bold transition-all border-b-4 min-h-[70px] flex items-center justify-center text-center shadow-lg ${
              selected === i
                ? 'bg-neon-purple border-purple-800 scale-95'
                : 'glass border-white/10 active:scale-95'
            } ${selected !== null && selected !== i ? 'opacity-40 grayscale' : ''}`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Player Status */}
      <div className="flex flex-wrap justify-center gap-2 max-h-16 overflow-y-auto">
        {gameState.players.map(p => {
          const isConfirming = confirmKickId === p.id;
          const isMe = p.id === clientState.myId;
          const hasAnswered = !!gameState.playerAnswers[p.id];

          return (
            <div
              key={p.id}
              className={`relative p-1.5 rounded-lg border-2 transition-all ${
                hasAnswered
                  ? 'border-neon-blue bg-neon-blue/20'
                  : 'border-white/5 grayscale opacity-30'
              } ${isConfirming ? 'border-red-600 bg-red-600/30 grayscale-0 opacity-100' : ''}`}
            >
              <span className="text-xl">{p.avatar}</span>
              {isHost && !isMe && (
                <button
                  onClick={() => handleKickClick(p.id)}
                  className={`absolute -top-1 -right-1 h-4 px-1 rounded-full flex items-center justify-center font-black text-[6px] shadow-lg z-10 ${
                    isConfirming
                      ? 'bg-red-600 text-white w-auto scale-110'
                      : 'bg-red-600 text-white w-4 hover:scale-110'
                  }`}
                >
                  {isConfirming ? 'K?' : '✕'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default GameLoopView;
